/**
 *
 * @module commerce
 * @desc commerce stored payment sequence
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/commerce.md#stored-payments
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../sdk';
import BaseSequence from './../base';
import Exception from './../../services/exception/entry';
import PriceOrderRequest from './../../services/commerce/priceOrderRequest';
import PriceOrderRequestAddress from './../../services/commerce/priceOrderRequestAddress';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    object,
    boolean
} = Check.assert;

/**
 *
 * @class CommerceStoredPayment
 * @extends {BaseSequence}
 *
 * @example const commerceStoredPayment = new SDK.Test.Sequences.CommerceStoredPayment(
 *     config, clearOnComplete
 * );
 *
 */
export default class CommerceStoredPayment extends BaseSequence {

    /**
     *
     * @param {Object} config configuration used to build a BootstrapConfiguration instance
     * @param {Boolean} [clearOnComplete=false] flag that determines if token refresh
     * should be cleared when the sequence is complete (useful when running tests)
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, clearOnComplete = false) {

        const errorMessage = {
            ctor: 'CommerceStoredPayment(config, clearOnComplete)',
            get config() {
                return `${this.ctor} config should be a plain Object`;
            },
            get clearOnComplete() {
                return `${this.ctor} clearOnComplete should be a Boolean`;
            }
        };

        if (Check.assigned(config)) {
            object(config, errorMessage.config, InvalidArgumentException);
        }

        boolean(clearOnComplete, errorMessage.clearOnComplete, InvalidArgumentException);

        super(config);

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.clearOnComplete = clearOnComplete;

    }

    /**
     *
     * @access public
     * @method execute
     * @desc runs the sequence to completion
     *
     * @example commerceStoredPayment.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, clearOnComplete } = this;
        const { clientId, clientApiKey, environment, debugEnabled } = config;

        const Services = SDK.Services;

        const ConfigurationHostName = Services.Configuration.ConfigurationHostName;

        const LineItem = SDK.Commerce.LineItem;
        const PaymentMethodInformation = SDK.Commerce.PaymentMethodInformation;
        const SubmitOrderWithCardPaymentMethodRequest = SDK.Commerce.SubmitOrderWithCardPaymentMethodRequest;

        const BootstrapConfiguration = Services.Configuration.BootstrapConfiguration;
        const BillingAddress = Services.Commerce.BillingAddress;
        const LineItemAttribute = Services.Commerce.LineItemAttribute;
        const Locale = Services.Commerce.Locale;
        const PaymentMethodResponse = SDK.Commerce.PaymentMethodResponse;
        const OrderCampaign = Services.Commerce.OrderCampaign;
        const RedemptionRequest = Services.Commerce.RedemptionRequest;
        const sku = ['SKU'];
        const campaignCode = 'campaignCode';
        const voucherCode = 'voucherCode';
        const zipCode = '07029';
        const city = 'NY';
        const state = 'NY';
        const country = 'US';

        const options = {
            sku,
            campaignCode,
            voucherCode,
            zipCode
        };

        return new Promise((resolve, reject) => {

            /** @type {SDK.Services.Configuration.BootstrapConfiguration} */
            const bootstrapConfiguration = new BootstrapConfiguration({
                clientId,
                clientApiKey,
                environment,
                debugEnabled,
                configHostName: ConfigurationHostName.dev
            });

            /** @type {SDK.SdkSession} */
            let sdkSession;

            /** @type {SDK.Commerce.CommerceApi} */
            let commerceApi;

            SDK.SdkSession.createSdkSession(bootstrapConfiguration).then((sa) => {

                /** @type {SDK.SdkSession} sa */
                sdkSession = sa;

                /** @test */
                assert.ok(sa);

                /** Establish Anonymous Session */
                return sdkSession.initialize();

            }).then(() => {

                /** @type {SDK.Commerce.CommerceApi} */
                commerceApi = sdkSession.commerceApi;

                return Promise.resolve();

            }).then(() => {

                const priceOrderRequest = new PriceOrderRequest(
                    ['productIdentifiers'],
                    0,
                    new OrderCampaign(options.campaignCode, options.voucherCode),
                    new PriceOrderRequestAddress({
                        zipCode,
                        city,
                        state,
                        country
                    })
                );

                return commerceApi.priceOrder(priceOrderRequest);

            }).then((priceOrderResponse) => {

                /** setup a default payment method */

                const firstName = 'firstName';
                const middleName = 'middleName';
                const lastName = 'lastName';
                const email = 'email@test.com';
                const line1 = 'line1';
                const line2 = 'line2';
                const line3 = 'line3';
                const postalCode = '12345';

                // only country is required for NL
                const addressObj = {
                    city,
                    country,
                    email,
                    firstName,
                    lastName,
                    line1,
                    line2,
                    line3,
                    middleName,
                    postalCode,
                    state
                };

                /** @type {SDK.Services.Commerce.BillingAddress} */
                const billingAddress = new BillingAddress(addressObj);

                const ownerFullName = `${firstName} ${middleName} ${lastName}`;
                const alternateName = 'alternateName';
                const brand = 'brand';
                const cardType = 'cardType';
                const firstSix = '123456';
                const isCorporate = false;
                const issuer = 'issuer';
                const lastFour = '1234';
                const token = 'token';
                const expiryMonth = 10;
                const expiryYear = 2020;
                const creditCardNumber = 'creditCardNumber';
                const isReusable = false;

                const paymentMethodInformationOptions = {
                    brand,
                    cardType,
                    country,
                    firstSix,
                    isCorporate,
                    issuer,
                    lastFour,
                    token,
                    creditCardNumber,
                    alternateName,
                    expiryMonth,
                    expiryYear,
                    ownerFullName,
                    billingAddress,
                    isReusable
                };

                /** @type {SDK.Services.Commerce.PaymentMethodInformation} */
                const paymentMethodInformation = new PaymentMethodInformation(paymentMethodInformationOptions);

                return commerceApi.createPaymentMethod(paymentMethodInformation, priceOrderResponse);

            }).then((paymentMethodId) => {

                /** @test */
                Check.assert.nonEmptyString(paymentMethodId);

                return commerceApi.getPaymentCard(paymentMethodId);

            }).then((paymentMethodResponse) => {

                /** @test */
                assert.ok(paymentMethodResponse instanceof PaymentMethodResponse);

                return commerceApi.getDefaultPaymentMethod();

            }).then((paymentMethodResponse) => {

                /** @test */
                assert.ok(paymentMethodResponse instanceof PaymentMethodResponse);

                return Promise.resolve();

            }).then(() => {

                const paymentMethodId = '12345';
                const cardSecurityCode = 'cardSecurityCode';
                const emailAddress = 'emailAddress@test.com';
                const promoCode = 'promoCode';
                const redemptionCode = 'redemptionCode';

                /** @type {SDK.Services.Commerce.Locale} */
                const locale = new Locale({
                    isoCountryCode: 'US',
                    isoLanguageCode: 'en'
                });

                /** @type {SDK.Services.Commerce.OrderCampaign} */
                const orderCampaign = new OrderCampaign('campaignCode', 'voucherCode');

                /** @type {SDK.Services.Commerce.RedemptionRequest} */
                const redemptionRequest = new RedemptionRequest(promoCode, redemptionCode);

                /** @type {SDK.Services.Commerce.LineItemAttribute} */
                const freeTrialAttribute = new LineItemAttribute(
                    'freetrial', // attributeName
                    'true' // attributeValue
                );

                /** @type {SDK.Commerce.LineItem} */
                const lineItems = [new LineItem(
                    'sku',
                    [freeTrialAttribute] // Array of LineItemAttributes
                )];

                const submitOrderWithCardPaymentMethodRequestOptions = {
                    lineItems,
                    orderCampaigns: [orderCampaign],
                    paymentMethodId,
                    locale,
                    cardSecurityCode,
                    emailAddress,
                    redemptionRequest
                };

                /** @type {SDK.Commerce.SubmitOrderWithCardPaymentMethodRequest} */
                const submitOrderWithCardPaymentMethodRequest = new SubmitOrderWithCardPaymentMethodRequest(
                    submitOrderWithCardPaymentMethodRequestOptions
                );

                return commerceApi.submitOrderWithPaymentMethod(submitOrderWithCardPaymentMethodRequest);

            }).then((guid) => {

                /** @test */
                Check.assert.nonEmptyString(guid);

                if (clearOnComplete) {
                    if (sdkSession.tokenManager) {
                        sdkSession.tokenManager.clearRetryRefresh();
                    }
                }

                return resolve();

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/commerce/commerceStoredPayment.js